import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material'
import axios from 'axios'
import { API_BASE, authHeaders, formatEUR, isFund, sortRows } from './api'
import { useLedger } from './LedgerContext'

const toSkDT = (iso) => (iso ? new Date(iso).toLocaleString('sk-SK') : '-')

export default function RecentTransactionsPanel({ filters }) {
  const ledger = useLedger()

  const addSourcesRef = useRef(ledger.addSourcesFromRows)
  const augmentUsersRef = useRef(ledger.augmentUserIds)

  useEffect(() => {
    addSourcesRef.current = ledger.addSourcesFromRows
    augmentUsersRef.current = ledger.augmentUserIds
  }, [ledger.addSourcesFromRows, ledger.augmentUserIds])

  const [pageRecent, setPageRecent] = useState(1)
  const [recentPanelRows, setRecentPanelRows] = useState([])
  const [recentPanelRowsAug, setRecentPanelRowsAug] = useState([])
  const [loadingRecent, setLoadingRecent] = useState(false)
  const [totalFiltered, setTotalFiltered] = useState({ sumAbs: 0, count: 0, loading: false })

  const toggleSort = (field) =>
    ledger.setSort(s =>
      s.field === field
        ? { field, dir: s.dir === 'asc' ? 'desc' : 'asc' }
        : { field, dir: 'asc' }
    )

  const recentAugFiltered = useMemo(() => {
    let arr = recentPanelRowsAug
    if (filters.userId && isFund(filters.account) && filters.account !== 'VAT_FUND') {
      arr = arr.filter(r => String(r.displayUserId) === String(filters.userId))
    }
    return arr
  }, [recentPanelRowsAug, filters.userId, filters.account])

  const recentAugSorted = useMemo(
    () => sortRows(recentAugFiltered, ledger.sort),
    [recentAugFiltered, ledger.sort]
  )

  const qsRecent = useMemo(() => {
    const p = new URLSearchParams()
    Object.entries(filters).forEach(([k, v]) => {
      if (v === '' || v == null) return
      if (k === 'userId' && isFund(filters.account) && filters.account !== 'VAT_FUND') return
      p.set(k, String(v))
    })
    const limit = Number(filters.limit) || 50
    p.set('limit', String(limit))
    p.set('offset', String((pageRecent - 1) * limit))
    return p.toString()
  }, [filters, pageRecent])

  useEffect(() => {
    setPageRecent(1)
  }, [filters])

  useEffect(() => {
    setLoadingRecent(true)
    axios
      .get(`${API_BASE}/api/ledger?${qsRecent}`, { headers: authHeaders() })
      .then(res => {
        const arr = Array.isArray(res.data) ? res.data : []
        setRecentPanelRows(arr)
        addSourcesRef.current(arr)
      })
      .catch(() => setRecentPanelRows([]))
      .finally(() => setLoadingRecent(false))
  }, [qsRecent])

  useEffect(() => {
    augmentUsersRef.current(recentPanelRows).then(setRecentPanelRowsAug)
  }, [recentPanelRows])

  const computeFilteredTotals = useCallback(async () => {
    setTotalFiltered(s => ({ ...s, loading: true }))
    let sumAbs = 0
    let count = 0
    const limit = 500
    let offset = 0

    for (;;) {
      const p = new URLSearchParams()
      Object.entries(filters).forEach(([k, v]) => {
        if (v) p.set(k, String(v))
      })
      p.set('limit', String(limit))
      p.set('offset', String(offset))

      const res = await axios.get(`${API_BASE}/api/ledger?${p}`, { headers: authHeaders() })
      const arr = Array.isArray(res.data) ? res.data : []
      for (const r of arr) sumAbs += Math.abs(Number(r.amount || 0))
      count += arr.length
      if (arr.length < limit) break
      offset += limit
    }

    setTotalFiltered({ sumAbs, count, loading: false })
  }, [filters])

  useEffect(() => {
    computeFilteredTotals()
  }, [computeFilteredTotals])

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', gap: 12, mb: 1 }}>
        <Typography variant="h6">Transakcie</Typography>
        <Typography variant="subtitle2">
          {totalFiltered.loading ? 'počítam…' : formatEUR(totalFiltered.sumAbs)}
        </Typography>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['txn_id','side','account','userId','amount','currency','source','description','createdAt'].map(col => (
                <TableCell key={col} onClick={() => toggleSort(col)}>
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {recentAugSorted.map(r => (
              <TableRow key={r.id}>
                <TableCell>{r.txn_id}</TableCell>
                <TableCell>{r.side}</TableCell>
                <TableCell>{r.account}</TableCell>
                <TableCell>{r.displayUserId}</TableCell>
                <TableCell align="right">{Number(r.amount || 0)}</TableCell>
                <TableCell>{r.currency || 'EUR'}</TableCell>
                <TableCell>{r.source}</TableCell>
                <TableCell>{r.description || '-'}</TableCell>
                <TableCell>{toSkDT(r.createdAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {loadingRecent && <Box sx={{ p: 2 }}>Načítavam…</Box>}
    </Paper>
  )
}
